import { useEffect, useState, useCallback } from 'react';
import { fetchKameleoonFlagsClientSide } from '@/utils/kameleoon/kameleoon-sdk';
import { TKamFlags } from '@/store/kameleoonFlags/kameleoonFlags.interface';
import { useStore } from '@/store/useStore';

interface UseKameleoonFlagsClientOptions {
  warehouseKey?: string;
  retryInterval?: number; // milliseconds
  maxRetries?: number;
  cookiePollInterval?: number; // milliseconds
  cookieMaxAttempts?: number;
  visitorCodeCookieName: string;
}

export const useKameleoonFlagsClient = (options: UseKameleoonFlagsClientOptions) => {
  const {
    warehouseKey,
    retryInterval = 2000, // 2 seconds
    maxRetries = 5, // 5 attempts
    cookiePollInterval = 1000, // 1 second
    cookieMaxAttempts = 30, // 30 seconds
    visitorCodeCookieName
  } = options;

  const [flags, setFlags] = useState<TKamFlags>({});
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [retryCount, setRetryCount] = useState(0);
  const [visitorCode, setVisitorCode] = useState<string | null>(null);
  const [isCookieLoading, setIsCookieLoading] = useState(true);
  const [cookieAttempts, setCookieAttempts] = useState(0);
  const [hasFetchedFlags, setHasFetchedFlags] = useState(false);

  const setKamFlags = useStore((state) => state.setKamFlags);

  // Cookie polling logic (integrated)
  const getCookieValue = useCallback((name: string): string | null => {
    if (typeof document === 'undefined') return null;
    
    const value = `; ${document.cookie}`;
    const parts = value.split(`; ${name}=`);
    if (parts.length >= 2) {
      const cookieValue = parts.pop()?.split(';').shift();
      return cookieValue || null;
    }
    return null;
  }, []);

  const checkForCookie = useCallback(() => {
    const cookieValue = getCookieValue(visitorCodeCookieName);
    
    if (cookieValue) {
      setVisitorCode(cookieValue);
      setIsCookieLoading(false);
      return true;
    }
    
    return false;
  }, [getCookieValue, visitorCodeCookieName]);

  // Cookie polling effect
  useEffect(() => {
    if (checkForCookie()) {
      return;
    }

    // Set up polling
    const intervalId = setInterval(() => {
      setCookieAttempts(prev => {
        const newAttempts = prev + 1;
        
        if (newAttempts >= cookieMaxAttempts) {
          setIsCookieLoading(false);
          clearInterval(intervalId);
          return newAttempts;
        }

        if (checkForCookie()) {
          clearInterval(intervalId);
          return newAttempts;
        }

        return newAttempts;
      });
    }, cookiePollInterval);

    return () => clearInterval(intervalId);
  }, [checkForCookie, cookiePollInterval, cookieMaxAttempts, visitorCodeCookieName]);

  const fetchFlags = useCallback(async () => {
    if (!visitorCode) {
      return;
    }
    // Use visitor code as warehouse key if no specific warehouse key is provided
    const effectiveWarehouseKey = warehouseKey || visitorCode;

    setIsLoading(true);
    setError(null);

    try {
      const fetchedFlags = await fetchKameleoonFlagsClientSide(
        visitorCode,
        effectiveWarehouseKey
      );
      setFlags(fetchedFlags);
      setKamFlags(fetchedFlags);
      if (Object.keys(fetchedFlags).length > 0) {
        setRetryCount(0); // Reset retry count on success
      }
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Unknown error';
      setError(errorMessage);
      console.error('Kameleoon: Error fetching flags:', errorMessage);
    } finally {
      setIsLoading(false);
    }
  }, [visitorCode, warehouseKey, setKamFlags]);

  // Effect to fetch flags when visitor code becomes available
  useEffect(() => {
    if (visitorCode && !isCookieLoading) {
      // Check if flags were already fetched on server-side
      const existingFlags = useStore.getState().kamFlags;
      
      if (existingFlags && Object.keys(existingFlags).length > 0) {
        setFlags(existingFlags);
        setHasFetchedFlags(true);
        return;
      }
      
      // Only fetch if we haven't fetched flags yet
      if (!hasFetchedFlags) {
        setHasFetchedFlags(true);
        fetchFlags();
      }
    }
  }, [visitorCode, isCookieLoading, hasFetchedFlags, fetchFlags]);

  // Effect to retry fetching flags if no flags were received
  useEffect(() => {
    if (!visitorCode || isLoading || retryCount >= maxRetries || isCookieLoading) {
      return;
    }

    // If we have a visitor code but no flags yet, retry after interval
    if (Object.keys(flags).length === 0 && retryCount < maxRetries) {
      const timeoutId = setTimeout(() => {
        setRetryCount(prev => prev + 1);
        fetchFlags();
      }, retryInterval);

      return () => clearTimeout(timeoutId);
    }
  }, [visitorCode, flags, isLoading, retryCount, maxRetries, retryInterval, fetchFlags, isCookieLoading]);

  return {
    flags,
    isLoading: isLoading || isCookieLoading,
    error,
    retryCount,
    cookieAttempts,
    refetch: fetchFlags,
  };
};

export default useKameleoonFlagsClient;
